import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { QUERY_MAPPING, groupParamsByKey } from 'libs/search-native/search-native-utils'
import { fetchSearchResults } from 'libs/search-native/search-native-service'
import {
  sortOrder,
  onPopState,
  sortOptions,
  initFilters,
  updateHandle,
  updateSearchQuery,
  updateOriginalQuery,
  setLocalSort,
  selectedValues,
  saveScrollPosition
} from 'snippets/plp-native/@store'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import PlpNativeGrid from 'snippets/plp-native/plp-native-grid'
import PlpNativeHeader from 'snippets/plp-native/plp-native-header/plp-native-header'
import PlpNativePagination from 'snippets/plp-native/plp-native-pagination/plp-native-pagination'
import PlpNativeFilterSlideout from 'snippets/plp-native/plp-native-filter/plp-native-filter-slideout'
import PlpNativeFilterType from 'snippets/plp-native/plp-native-filter/plp-native-filter-type'

function PlpNative(props) {
  const [articleTotalResults, setArticleTotalResults] = useState(0)
  const [pageTotalResults, setPageTotalResults] = useState(0)
  const [productTotalResults, setProductTotalResults] = useState(0)
  const [isFetching, setIsFetching] = useState(true)
  const [filterType, setFilterType] = useState('product')
  const { handle } = props
  const { filters, perPage } = window.collectionState
  const { isDesktop, isMobile } = useDetectBreakpoint()
  const { href, pathname } = window.location
  const paramsString = href.split('?')[1]
  const paramsDefault = groupParamsByKey(new URLSearchParams(paramsString), filters)

  const openFilterRef = useRef()

  const isSearchPage = (path) => path === GM_STATE.routes.search_url

  const handleInitFilters = () => {
    if (pathname.includes(QUERY_MAPPING.search.path) || isSearchPage(pathname)) {
      initFilters({
        searchQuery: paramsDefault.searchQuery,
        originalQuery: paramsDefault.originalQuery,
        ...paramsDefault
      })
      updateSearchQuery(paramsDefault.searchQuery)
      updateOriginalQuery(paramsDefault.originalQuery)
    } else {
      initFilters({
        handle,
        ...paramsDefault
      })
      updateHandle(handle)
    }
  }

  const addEventPopstate = () => {
    window.addEventListener('popstate', (event) => {
      const finalParams = event.state !== null ? event.state.params : {}
      const finalUrl = event.state !== null ? event.state.url : null

      finalParams.forceUpdate = true

      if ((finalUrl && finalUrl.includes(QUERY_MAPPING.search.path)) || isSearchPage(finalUrl)) {
        initFilters({
          searchQuery: finalParams.searchQuery,
          ...finalParams
        })
        updateSearchQuery(finalParams.searchQuery)
      } else {
        onPopState({
          handle,
          ...finalParams
        })
        updateHandle(handle)
      }
    })
  }

  const fetchDataTotalResults = async () => {
    try {
      setIsFetching(true)

      const [articles, pages, products] = await Promise.all([
        fetchSearchResults({ searchQuery: paramsDefault.searchQuery, searchType: 'article' }, true),
        fetchSearchResults({ searchQuery: paramsDefault.searchQuery, searchType: 'page' }, true),
        fetchSearchResults({ searchQuery: paramsDefault.searchQuery, searchType: 'product' }, true)
      ])

      setArticleTotalResults(articles?.pagination?.totalProducts)
      setPageTotalResults(pages?.pagination?.totalProducts)
      setProductTotalResults(products?.pagination?.totalProducts)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setIsFetching(false)
    }
  }

  const countFilterSelected = useMemo(() => {
    const cloneObj = { ...selectedValues.value }

    if (cloneObj['filter.p.m.seo.hidden']) {
      delete cloneObj['filter.p.m.seo.hidden']
    }

    return Object.keys(cloneObj).length
  }, [selectedValues.value])

  const showFiltersAndHeader =
    !isSearchPage(pathname) || (isSearchPage(pathname) && filterType === 'product')

  const handleSaveScrollPosition = () => {
    const payloadHandle = handle || 'search'

    saveScrollPosition(payloadHandle)
  }

  useEffect(() => {
    handleInitFilters()
    addEventPopstate()

    if (isSearchPage(pathname)) {
      fetchDataTotalResults()
    }
  }, [])

  useEffect(() => {
    if (sortOptions.value?.length <= 0) return

    const getCurrentSort = (orders) =>
      orders ? sortOptions.value.find((item) => orders.direction === item.direction) : sortOptions.value[0]

    setLocalSort(getCurrentSort(sortOrder.value))
  }, [sortOptions.value, sortOrder.value])

  return (
    <>
      {isSearchPage(pathname) && (
        !isFetching ? (
          <div className="container py-5 border-b border-neutral-900 flex gap-6 lg:py-8">
            <PlpNativeFilterType
              type="product"
              filterType={filterType}
              setFilterType={setFilterType}
              totalResults={productTotalResults}
            />
            <PlpNativeFilterType
              type="article"
              filterType={filterType}
              setFilterType={setFilterType}
              totalResults={articleTotalResults}
            />
            <PlpNativeFilterType
              type="page"
              filterType={filterType}
              setFilterType={setFilterType}
              totalResults={pageTotalResults}
            />
          </div>
        ) : (
          <div className="container py-5 border-b border-neutral-900 flex gap-6 lg:py-8 animate-pulse">
            <div className="h-5 w-20 bg-skeleton" />
            <div className="h-5 w-20 bg-skeleton" />
            <div className="h-5 w-20 bg-skeleton" />
          </div>
        )
      )}
      {showFiltersAndHeader ? (
        <>
          <PlpNativeFilterSlideout
            countFilterSelected={countFilterSelected}
            openFilterRef={openFilterRef}
            isMobile={isMobile}
            isDesktop={isDesktop}
          />
          <PlpNativeHeader />
        </>
      ) : null}
      <div className="js-plp-native-grid flex-1 border-b border-neutral-900 overflow-hidden">
        <PlpNativeGrid
          perPage={perPage}
          filterType={filterType}
          countFilterSelected={countFilterSelected}
          onClickProduct={handleSaveScrollPosition}
        />
        <PlpNativePagination filterType={filterType} />
      </div>
    </>
  )
}

export default PlpNative
